/**
 * "Traveling together": the customer's card on a trip our team has linked to
 * other bookings (server/trip-links.js). Founder 2026-10-08: "show a beautiful
 * small card to customer that shows trip was linked. That way they have peace
 * of mind."
 *
 * PRIVACY: the customer's own linked trips show by reference, route and date
 * (they're theirs). A linked trip on anyone else's account is only "another
 * Defera booking": GET /api/user/trip-links never returns its reference,
 * travelers or route, so there is nothing here to leak. Staff notes never
 * reach the customer either.
 *
 * Copy is in DEFERA_KNOWLEDGE_BASE (website-promise parity): the card says the
 * team knows the trips travel together. It is not a seat assignment.
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { Users, Link2 } from 'lucide-react';
import { TripLinks } from '@/api/entities';
import { cn } from '@/lib/utils';

function shortDay(value) {
  if (!value) return '';
  try {
    return format(parseISO(String(value)), 'MMM d');
  } catch {
    return '';
  }
}

/** "A", "A and B", "A, B and C". */
function joinList(items) {
  if (items.length <= 1) return items[0] || '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** "your trip JZAHBD", "your trips JZAHBD and K7Q2XD and another Defera booking", … */
function linkedWith(ownTrips, otherBookings) {
  const items = ownTrips.map((t, i) => (i === 0 ? `${ownTrips.length === 1 ? 'your trip' : 'your trips'} ${t.reference}` : t.reference));
  if (otherBookings === 1) items.push('another Defera booking');
  else if (otherBookings > 1) items.push(`${otherBookings} other Defera bookings`);
  return joinList(items);
}

/** One line per pill: on a narrow phone a pill moves down whole rather than splitting "Nov 6". */
const pill = 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[12px] font-normal leading-none';

export default function TravelingTogetherCard({ thisReference = null, ownTrips = [], otherBookings = 0, className }) {
  if (!ownTrips.length && !otherBookings) return null;
  return (
    <motion.section
      aria-label="Traveling together"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut', delay: 0.08 }}
      className={cn('relative mb-6 overflow-hidden rounded-2xl border border-emerald-300/20', className)}
    >
      {/* Calm, layered wash: static on mobile, a soft glow from sm up. */}
      <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-emerald-400/[0.08] via-white/[0.02] to-transparent" />
      <div aria-hidden className="hidden sm:block pointer-events-none absolute -right-16 -top-16 w-48 h-48 rounded-full bg-emerald-300/10 blur-3xl" />
      <div aria-hidden className="hidden sm:block pointer-events-none absolute -left-20 -bottom-24 w-56 h-56 rounded-full bg-brand-500/5 blur-3xl" />

      <div className="relative flex items-start gap-4 p-4 sm:p-5">
        <motion.div
          initial={{ scale: 0.85, rotate: -6 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 220, damping: 16, delay: 0.2 }}
          className="relative flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-emerald-400/10 border border-emerald-300/25 flex items-center justify-center shadow-[0_0_24px_-8px_rgba(52,211,153,0.35)]"
        >
          <Users className="w-5 h-5 text-emerald-200" strokeWidth={2} aria-hidden />
        </motion.div>

        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-emerald-200/80">Traveling together</p>
          <h3 className="mt-0.5 text-[15px] sm:text-base font-medium text-white leading-snug">Your trips are linked</h3>
          <p className="mt-1 text-[12.5px] sm:text-[13px] font-normal text-white/65 leading-relaxed">
            Our concierge team has linked this trip with {linkedWith(ownTrips, otherBookings)}, so we know you&apos;re traveling together.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {thisReference && (
              <span className={cn(pill, 'border-white/15 bg-white/[0.06] text-white/85')}>
                <span className="font-mono tracking-wide">{thisReference}</span>
                <span className="text-white/45">This trip</span>
              </span>
            )}
            <Link2 className="w-3.5 h-3.5 text-emerald-200/70 flex-shrink-0" aria-hidden />
            {ownTrips.map((t) => (
              <Link
                key={t.booking_id || t.reference}
                to={`/trips?tripId=${encodeURIComponent(t.booking_id || t.reference)}`}
                className={cn(pill, 'border-emerald-300/30 bg-emerald-400/10 text-emerald-50 hover:bg-emerald-400/15 transition-colors')}
                aria-label={`Open your trip ${t.reference}`}
              >
                <span className="font-mono tracking-wide">{t.reference}</span>
                {[t.route, shortDay(t.departs_on)].filter(Boolean).length > 0 && (
                  <span className="text-emerald-100/60">{[t.route, shortDay(t.departs_on)].filter(Boolean).join(' · ')}</span>
                )}
              </Link>
            ))}
            {otherBookings > 0 && (
              <span className={cn(pill, 'border-white/10 bg-white/[0.04] text-white/70')}>
                {otherBookings === 1 ? 'Another Defera booking' : `${otherBookings} other Defera bookings`}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.section>
  );
}

TravelingTogetherCard.propTypes = {
  thisReference: PropTypes.string,
  ownTrips: PropTypes.arrayOf(PropTypes.shape({
    booking_id: PropTypes.string,
    reference: PropTypes.string,
    route: PropTypes.string,
    departs_on: PropTypes.string,
  })),
  otherBookings: PropTypes.number,
  className: PropTypes.string,
};

/** The card on the customer's own trip, loaded from GET /api/user/trip-links. */
export function CustomerTravelingTogether({ bookingId, thisReference = null }) {
  const [view, setView] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setView(null);
    if (!bookingId) return undefined;
    TripLinks.forMyTrip(bookingId)
      .then((d) => { if (!cancelled) setView(d); })
      .catch(() => { /* non-critical: no card */ });
    return () => { cancelled = true; };
  }, [bookingId]);
  if (!view?.linked) return null;
  return (
    <TravelingTogetherCard
      thisReference={thisReference}
      ownTrips={Array.isArray(view.own_trips) ? view.own_trips : []}
      otherBookings={Number(view.other_bookings) || 0}
    />
  );
}

CustomerTravelingTogether.propTypes = {
  bookingId: PropTypes.string,
  thisReference: PropTypes.string,
};
